import {
  Component, Inject, OnInit,
} from '@angular/core';
import { UnifiedRegistrationApiService } from '../unified-registration.api.service';

@Component({
  selector: 'ut-unified-set-password',
  templateUrl: './set-password.component.html',
  standalone: false,
})
export class UnifiedSetPasswordComponent implements OnInit {
  emailCode: string | null = null;
  passwordCode: string | null = null;
  linkValid = false;
  loading = true;
  submissionInProgress = false;

  // User information
  name = '';
  email = '';

  // Password data
  password = '';
  passwordVisible = false;

  error: string | null = null;
  requirementMet = ', requirement is met.';
  requirementNotMet = ', requirement is not met.';
  lengthRequirement = 'Must be at least 10 characters long';
  uppercaseRequirement = 'Must contain at least one uppercase letter';
  lowercaseRequirement = 'Must contain at least one lowercase letter';
  numberRequirement = 'Must contain at least one number';
  emailRequirement = 'Cannot contain your email address';

  constructor(
    @Inject('$location') private $location: any,
    @Inject('$window') private $window: any,
    @Inject('$state') private $state: any,
    @Inject('UserService') private UserService: any,
    private api: UnifiedRegistrationApiService,
  ) { }

  ngOnInit(): void {
    this.initializeComponent();
  }

  private initializeComponent(): void {
    const queryParams = this.$location.search();

    this.emailCode = queryParams.code;
    this.passwordCode = queryParams.userCode;
    this.linkValid = !!(this.emailCode && this.passwordCode);

    // Check if user is already logged in
    this.UserService.me().then((res: any) => {
      if (res !== null) {
        this.$window.location.href = '/login';
        return;
      }

      this.loading = false;

      if (this.linkValid) {
        this.fetchUserInfo();
      }
    }).catch(() => {
      this.loading = false;
    });
  }

  private fetchUserInfo(): void {
    if (!this.passwordCode) return;

    this.api.getUserInfoByCode(this.passwordCode).subscribe({
      next: (userInfo) => {
        this.name = userInfo.name || '';
        this.email = userInfo.email || '';
      },
      error: (error) => {
        if (error?.status === 400) {
          // Link expired
          this.UserService.resendExpiredSetPasswordEmail(this.emailCode);
          this.$state.go('setpassword-expired', { emailCode: this.emailCode });
        } else if (error?.status === 409) {
          // Already verified
          this.$state.go('setpassword-already-verified');
        } else {
          // Redirect to error page if we can't fetch user info
          this.$state.go('setpassword-error');
        }
      },
    });
  }

  hasLeadingOrTrailingSpaces(): boolean {
    return this.password !== this.password.trim();
  }

  hasMinLength(): boolean {
    return this.password.length >= 10;
  }

  hasUppercase(): boolean {
    return /[A-Z]/.test(this.password);
  }

  hasLowercase(): boolean {
    return /[a-z]/.test(this.password);
  }

  hasNumber(): boolean {
    return /[0-9]/.test(this.password);
  }

  doesNotContainEmail(): boolean {
    if (!this.email || !this.password) return true;
    return !this.password.toLowerCase().includes(this.email.toLowerCase());
  }

  isPasswordValid(): boolean {
    return !this.hasLeadingOrTrailingSpaces()
      && this.hasMinLength()
      && this.hasUppercase()
      && this.hasLowercase()
      && this.hasNumber()
      && this.doesNotContainEmail();
  }

  togglePasswordVisibility(): void {
    this.passwordVisible = !this.passwordVisible;
  }

  onSubmit(): void {
    if (!this.isPasswordValid() || this.submissionInProgress) {
      return;
    }

    this.submissionInProgress = true;
    this.error = null;
    this.confirmEmailAndUpdatePassword();
  }

  private confirmEmailAndUpdatePassword(): void {
    if (!this.emailCode || !this.passwordCode) {
      this.submissionInProgress = false;
      this.error = 'Missing verification codes';
      return;
    }

    // First confirm the email
    this.UserService.confirmEmail(this.emailCode)
      .then(() => this.UserService.updatePassword(this.password, this.passwordCode))
      .then(() => {
        this.submissionInProgress = false;
        // Redirect to login page on success
        this.$window.location.href = '/login';
      })
      .catch((error: any) => {
        this.submissionInProgress = false;

        // Handle specific error cases with redirects to dedicated pages
        if (error?.status === 404 || error?.status === 410) {
          // Link expired
          this.$state.go('setpassword-expired', this.$location.search());
        } else if (error?.status === 409) {
          // Already verified
          this.$state.go('setpassword-already-verified');
        } else {
          // General error - show inline error message for form validation issues
          this.error = this.getErrorMessage(error);
        }
      });
  }

  private getErrorMessage(error: any): string {
    // Extract meaningful error message from the response
    if (error?.data?.errors?.[0]?.message) {
      return error.data.errors[0].message;
    }
    if (error?.data?.message) {
      return error.data.message;
    }
    return 'An error occurred while setting your password. Please try again.';
  }
}

export default UnifiedSetPasswordComponent;
